/**
 * RL-984 refill and outside-pharmacy requests from the patient app, in the
 * doctor's inbox (RL-991 permissions). Shapes follow ommri-care-api
 * `prescription-requests.examples.ts` (STAFF_RX_REQUEST*, RXQ).
 */
export const RX_REQUEST_STATUSES = [
  'pending',
  'approved',
  'refused',
  'withdrawn',
] as const;
export type RxRequestStatus = (typeof RX_REQUEST_STATUSES)[number];
export type RxRequestStatusFilter = RxRequestStatus | 'all';

export const RX_REQUEST_KINDS = ['refill', 'outside_pharmacy'] as const;
export type RxRequestKind = (typeof RX_REQUEST_KINDS)[number];

export type RxRouteReason =
  'prescriber' | 'branch_inbox' | 'claimed' | 'reassigned';

export type RxExcludedReason =
  | 'controlled'
  | 'psychotropic'
  | 'poison_group_a'
  | 'not_in_catalogue'
  | 'already_dispensed';

export type RxWithdrawnReason =
  'patient' | 'account_deleted' | 'prescription_cancelled';

export type RxDocumentRevokedReason =
  'supplied_at_clinic' | 'prescription_cancelled' | 'other';

export interface StaffRef {
  id: string;
  fullName: string;
}

export interface RxRequestItem {
  itemId: string | null;
  medicine: string;
  dose: string | null;
  frequency: string | null;
  durationDays: number | null;
  quantity: number | null;
  unit: string | null;
  /** Batch 17: the prescribing allergy check now; approve is blocked while one is listed. */
  allergyAlerts: string[];
  /**
   * RL-1181: the same alerts (same order) with the allergy behind each.
   * Optional until every API this build talks to sends it.
   */
  allergyAlertDetails?: RxAllergyAlertDetail[];
}

/** One `allergyAlerts` entry with its allergy (RL-1181). */
export interface RxAllergyAlertDetail {
  text: string;
  substance: string;
  /** true = patient-reported in the app, not confirmed yet. */
  reported: boolean;
  /** RL-1171: recorded on a merged record of this patient. */
  fromMergedRecord: boolean;
}

export interface RxExcludedItem {
  itemId: string;
  medicine: string;
  reason: RxExcludedReason;
}

export interface RxPatientAllergy {
  substance: string;
  reaction: string | null;
  /** reported = patient-reported in the app, not confirmed yet (blocks like a confirmed one). */
  status: 'confirmed' | 'reported';
  /** RL-1171: recorded on another record of the patient's merged-record family. */
  fromMergedRecord: boolean;
  fromRecord: StaffRef | null;
}

export interface RxRequestDocument {
  id: string;
  number: string;
  verificationCode: string;
  revokedAt: string | null;
  revokedReason: RxDocumentRevokedReason | null;
}

export interface StaffRxRequest {
  id: string;
  kind: RxRequestKind;
  status: RxRequestStatus;
  patient: StaffRef;
  branch: { id: string; name: string };
  prescription: {
    id: string;
    number: string | null;
    status: string;
    signedAt: string | null;
    prescriber: StaffRef | null;
    dispensingStatus: string;
  };
  /** null = the branch's doctors (inbox). */
  routedTo: StaffRef | null;
  routeReason: RxRouteReason;
  /** false = oversight view (manager): no medicines, allergies or refusal text. */
  detailsShown: boolean;
  items: RxRequestItem[] | null;
  excludedItems: RxExcludedItem[] | null;
  patientAllergies: RxPatientAllergy[] | null;
  allergyOverrideReason: string | null;
  refills: { approvedBefore: number; max: number } | null;
  waitingSince: string | null;
  decidedAt: string | null;
  decidedBy: StaffRef | null;
  refuseReason: string | null;
  document: RxRequestDocument | null;
  withdrawnAt: string | null;
  withdrawnReason: RxWithdrawnReason | null;
  createdAt: string;
  version: number;
}

export interface RxRequestFilters {
  status: RxRequestStatusFilter;
  kind: RxRequestKind | '';
}

export interface ApproveRxRequestInput {
  version: number;
  allergyOverride?: { reason: string };
}

export interface RefuseRxRequestInput {
  version: number;
  reason: string;
}

export interface RouteRxRequestInput {
  doctorId: string | null;
  version: number;
}

/** 422 PRESCRIPTION_BLOCKED extensions (allergy clash at approval). */
export interface RxAllergyBlock {
  lines: string[];
  blockedItems: {
    itemId: string | null;
    medicine: string;
    messages: string[];
  }[];
}

export const RX_REQUEST_LIMITS = {
  refuseReasonMax: 300,
  overrideReasonMin: 2,
  overrideReasonMax: 300,
} as const;

export const RX_REQUEST_PERMISSIONS = {
  read: 'doctor_inbox:read',
  manage: 'doctor_inbox:manage',
  oversee: 'doctor_inbox:oversee',
} as const;
